import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'

export const Dialog = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger

export function DialogOverlay({ className, ...props }: DialogPrimitive.DialogOverlayProps) {
  return (
    <DialogPrimitive.Overlay
      className={cn('fixed inset-0 z-40 bg-black/50', className)}
      {...props}
    />
  )
}

export interface DialogContentProps extends DialogPrimitive.DialogContentProps {
  /**
   * Skips rendering `DialogOverlay` and widens the hit area that
   * `onInteractOutside` ignores. Used by the split dialog, which --
   * uniquely among this milestone's dialogs -- needs the user to be able
   * to click the timeline *behind* it while it's open (one of the three
   * independent ways to pick a split point). A full-screen overlay would
   * block exactly that interaction.
   */
  withoutOverlay?: boolean
}

export function DialogContent({
  className,
  children,
  withoutOverlay = false,
  ...props
}: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      {!withoutOverlay && <DialogOverlay />}
      <DialogPrimitive.Content
        // WAI-ARIA's Dialog (Modal) pattern requires `aria-modal="true"`
        // on a modal dialog so assistive tech treats the rest of the page
        // as inert -- confirmed by reading this installed Radix version's
        // source (`@radix-ui/react-dialog/dist/index.mjs`) that it sets
        // `role="dialog"` but does *not* set `aria-modal` itself, despite
        // handling the actual modal *behavior* (focus trap, inert
        // background) correctly. Defaulted here, before the `{...props}`
        // spread, so the one genuinely non-modal dialog (`SplitDialog`,
        // which also passes `modal={false}` to the parent `Dialog`) can
        // override it to `false`.
        aria-modal="true"
        className={cn(
          // Inset from the screen edges and scrollable on phones, where a long dialog (or the on-screen keyboard) would otherwise push its buttons off-screen.
          'border-border bg-card text-card-foreground fixed top-1/2 left-1/2 z-50 max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border p-4 shadow-lg focus:outline-none sm:p-5',
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close
          aria-label="Close dialog"
          className="text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring absolute top-3 right-3 rounded-sm p-1 outline-none focus-visible:ring-2"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

export function DialogTitle({ className, ...props }: DialogPrimitive.DialogTitleProps) {
  return (
    <DialogPrimitive.Title
      className={cn('text-card-foreground mb-1 pr-6 text-base font-semibold', className)}
      {...props}
    />
  )
}

export function DialogDescription({ className, ...props }: DialogPrimitive.DialogDescriptionProps) {
  return (
    <DialogPrimitive.Description
      className={cn('text-muted-foreground mb-3 text-sm', className)}
      {...props}
    />
  )
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mt-4 flex items-center justify-end gap-2', className)} {...props} />
}
